/**
 * @fileoverview Sync status button for the Documents (Gather) page.
 *
 * Compact popover summarising the job's integration state — folder syncs
 * (OneDrive / SharePoint) and Xero report imports — with each row and the
 * footer linking to Job Settings → Integrations where syncs are managed.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { FunctionReturnType } from "convex/server";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, ArrowRight, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  ACCOUNT_PROVIDERS,
  PROVIDER_LABELS,
  type PipesProvider,
} from "@/routes/_app/_auth/dashboard/-microsoft-providers";
import { ProviderLogo } from "@/routes/_app/_auth/dashboard/-ui.microsoft-account-section";

type ConnectionWithRun = FunctionReturnType<
  typeof api.integrations.workOsSync.queries.getJobIntegrations
>[number];

type RowState = "ok" | "warning" | "pending" | "none";

interface ProviderRow {
  provider: PipesProvider;
  state: RowState;
  /** What is connected — folder path or Xero organisation name. */
  detail: string | null;
  /** Current status — "synced 2h ago", "needs reauthorization", … */
  status: string;
}

function buildRow(
  provider: PipesProvider,
  connection: ConnectionWithRun | undefined,
): ProviderRow {
  if (!connection) {
    return { provider, state: "none", detail: null, status: "not set up" };
  }

  const hasWarning =
    connection.status === "needs_reauthorization" ||
    Boolean(connection.lastSyncError) ||
    connection.latestRun?.status === "failed";
  const warningStatus =
    connection.status === "needs_reauthorization"
      ? "needs reauthorization"
      : "last sync failed";

  if (provider === "xero") {
    const detail = connection.xeroTenantName ?? "Xero organisation";
    if (hasWarning) {
      return { provider, state: "warning", detail, status: warningStatus };
    }
    if (connection.lastSyncCompletedAt) {
      return {
        provider,
        state: "ok",
        detail,
        status: `imported ${formatDistanceToNow(connection.lastSyncCompletedAt, { addSuffix: true })}`,
      };
    }
    return {
      provider,
      state: "pending",
      detail,
      status: "linked — no reports imported yet",
    };
  }

  const detail = connection.folderPath || connection.driveName || "(root)";
  if (hasWarning) {
    return { provider, state: "warning", detail, status: warningStatus };
  }
  if (connection.latestRun?.status === "running") {
    return { provider, state: "pending", detail, status: "sync in progress…" };
  }
  const syncedAt =
    connection.lastSyncCompletedAt ?? connection.latestRun?.completedAt;
  if (syncedAt) {
    return {
      provider,
      state: "ok",
      detail,
      status: `synced ${formatDistanceToNow(syncedAt, { addSuffix: true })}`,
    };
  }
  return { provider, state: "pending", detail, status: "not synced yet" };
}

const STATE_DOT_CLASS: Record<RowState, string> = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  pending: "bg-blue-500",
  none: "bg-muted-foreground/40",
};

/** Human-readable label per outbound op kind. */
const OP_KIND_LABELS: Record<string, string> = {
  folderCreate: "Couldn't create folder on the drive",
  folderRename: "Couldn't rename folder on the drive",
  folderMove: "Couldn't move folder on the drive",
  folderDelete: "Couldn't delete folder on the drive",
  fileRename: "Couldn't rename file on the drive",
  fileMove: "Couldn't move file on the drive",
  fileDelete: "Couldn't delete file on the drive",
};

const MAX_VISIBLE_FAILED_OPS = 5;

function truncateError(message: string): string {
  return message.length > 80 ? `${message.slice(0, 80)}…` : message;
}

export function SyncStatusButton({ jobId }: { jobId: Id<"jobs"> }) {
  const navigate = useNavigate();
  const { data: connections } = useQuery(
    convexQuery(api.integrations.workOsSync.queries.getJobIntegrations, {
      jobId,
    }),
  );
  const { data: outboundOps } = useQuery(
    convexQuery(api.integrations.workOsSync.queries.listOutboundOpsForJob, {
      jobId,
    }),
  );

  const retryOp = useMutation({
    mutationFn: useConvexMutation(
      api.integrations.workOsSync.mutations.retryOutboundOp,
    ),
    onSuccess: () => toast.success("Retrying sync"),
    onError: (error) => toast.error(error.message),
  });

  const failedOps = (outboundOps ?? []).filter((op) => op.status === "failed");
  const activeOpCount = (outboundOps ?? []).filter(
    (op) => op.status === "pending" || op.status === "running",
  ).length;
  const visibleFailedOps = failedOps.slice(0, MAX_VISIBLE_FAILED_OPS);
  const hiddenFailedCount = failedOps.length - visibleFailedOps.length;

  const byProvider = new Map(
    (connections ?? [])
      .filter((c) => c.status !== "disconnected")
      .map((c) => [c.provider as PipesProvider, c]),
  );
  const rows = ACCOUNT_PROVIDERS.map((provider) =>
    buildRow(provider, byProvider.get(provider)),
  );

  const overall: RowState =
    failedOps.length > 0 || rows.some((r) => r.state === "warning")
      ? "warning"
      : rows.some((r) => r.state === "ok")
        ? "ok"
        : rows.some((r) => r.state === "pending")
          ? "pending"
          : "none";

  const goToIntegrations = () =>
    void navigate({
      to: "/dashboard/jobs/$jobId/settings",
      params: { jobId },
      search: { tab: "integrations" },
    });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 bg-background px-2 text-xs"
        >
          <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
          Sync status
          <span
            className={cn(
              "ml-2 h-2 w-2 rounded-full",
              STATE_DOT_CLASS[overall],
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="space-y-0.5">
          {rows.map((row) => (
            <button
              key={row.provider}
              type="button"
              onClick={goToIntegrations}
              className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
            >
              <ProviderLogo provider={row.provider} className="h-4 w-4" />
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block truncate",
                    row.state === "none" && "text-muted-foreground",
                  )}
                >
                  {PROVIDER_LABELS[row.provider]}
                  {row.detail && (
                    <span className="text-muted-foreground">
                      {" "}
                      — {row.detail}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    "block text-xs",
                    row.state === "warning"
                      ? "text-amber-600 dark:text-amber-500"
                      : "text-muted-foreground",
                  )}
                >
                  {row.status}
                </span>
              </span>
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  STATE_DOT_CLASS[row.state],
                )}
              />
            </button>
          ))}
          {activeOpCount > 0 && (
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {activeOpCount} change{activeOpCount === 1 ? "" : "s"} syncing to
              the drive…
            </p>
          )}
        </div>
        {visibleFailedOps.length > 0 && (
          <div className="mt-1 space-y-0.5 border-t pt-1">
            {visibleFailedOps.map((op) => (
              <div
                key={op._id}
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm"
              >
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {OP_KIND_LABELS[op.kind] ?? "Couldn't sync to the drive"}
                  </span>
                  {op.lastError && (
                    <span
                      className="block truncate text-xs text-amber-600 dark:text-amber-500"
                      title={op.lastError}
                    >
                      {truncateError(op.lastError)}
                    </span>
                  )}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  disabled={retryOp.isPending}
                  onClick={() => retryOp.mutate({ opId: op._id })}
                >
                  Retry
                </Button>
              </div>
            ))}
            {hiddenFailedCount > 0 && (
              <p className="px-2 py-1 text-xs text-muted-foreground">
                +{hiddenFailedCount} more
              </p>
            )}
          </div>
        )}
        <div className="mt-1 border-t pt-1">
          <button
            type="button"
            onClick={goToIntegrations}
            className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Manage on Integrations tab
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
